import type {
  CreatePurchaseRequestPayload,
  CreateQuotationPayload,
  PurchaseAcquisitionType,
  PurchaseCriticality,
  PurchasePriority,
  PurchaseRequest,
  PurchaseRequestItem,
} from '../api';

export interface PurchaseRequestItemFormValue {
  key: string;
  description: string;
  quantity: string;
  unit: string;
  estimatedUnitPrice: string;
  specifications: string;
}

export interface PurchaseRequestFormValues {
  title: string;
  justification: string;
  requestingArea: string;
  costCenterId: string;
  clientId: string;
  projectId: string;
  category: string;
  acquisitionType: PurchaseAcquisitionType;
  priority: PurchasePriority;
  neededBy: string;
  scope: string;
  technicalRequirements: string;
  functionalRequirements: string;
  criticality: PurchaseCriticality;
  budgetStatus: string;
  impact: string;
  needsDataAccess: boolean;
  needsSystemsAccess: boolean;
  needsEnvironmentAccess: boolean;
  needsInternalInformation: boolean;
  accessDetails: string;
  items: PurchaseRequestItemFormValue[];
}

export type PurchaseFormErrors = Record<string, string>;

export function emptyPurchaseItem(key = crypto.randomUUID()): PurchaseRequestItemFormValue {
  return {
    key,
    description: '',
    quantity: '1',
    unit: 'un',
    estimatedUnitPrice: '',
    specifications: '',
  };
}

export function emptyPurchaseRequestForm(): PurchaseRequestFormValues {
  return {
    title: '',
    justification: '',
    requestingArea: '',
    costCenterId: '',
    clientId: '',
    projectId: '',
    category: '',
    acquisitionType: 'product',
    priority: 'normal',
    neededBy: '',
    scope: '',
    technicalRequirements: '',
    functionalRequirements: '',
    criticality: 'medium',
    budgetStatus: 'planned',
    impact: '',
    needsDataAccess: false,
    needsSystemsAccess: false,
    needsEnvironmentAccess: false,
    needsInternalInformation: false,
    accessDetails: '',
    items: [emptyPurchaseItem()],
  };
}

export function purchaseRequestFormFromRequest(request: PurchaseRequest): PurchaseRequestFormValues {
  return {
    title: request.title,
    justification: request.justification,
    requestingArea: request.requestingArea ?? '',
    costCenterId: request.costCenterId ?? '',
    clientId: request.clientId ?? '',
    projectId: request.projectId ?? '',
    category: request.category,
    acquisitionType: request.acquisitionType,
    priority: request.priority,
    neededBy: request.neededBy ?? '',
    scope: request.scope ?? '',
    technicalRequirements: request.technicalRequirements ?? '',
    functionalRequirements: request.functionalRequirements ?? '',
    criticality: request.criticality,
    budgetStatus: request.budgetStatus ?? '',
    impact: request.impact ?? '',
    needsDataAccess: request.accessNeeds?.data ?? false,
    needsSystemsAccess: request.accessNeeds?.systems ?? false,
    needsEnvironmentAccess: request.accessNeeds?.environments ?? false,
    needsInternalInformation: request.accessNeeds?.internalInformation ?? false,
    accessDetails: request.accessNeeds?.details ?? '',
    items: request.items.map((item) => ({
      key: item.id,
      description: item.description,
      quantity: String(item.quantity),
      unit: item.unit,
      estimatedUnitPrice: item.estimatedUnitPrice == null ? '' : String(item.estimatedUnitPrice),
      specifications: item.specifications ?? '',
    })),
  };
}

function positiveNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function validatePurchaseRequestForm(values: PurchaseRequestFormValues): PurchaseFormErrors {
  const errors: PurchaseFormErrors = {};
  if (values.title.trim().length < 3) errors.title = 'Informe um título com pelo menos 3 caracteres.';
  if (values.justification.trim().length < 10) errors.justification = 'Detalhe a justificativa com pelo menos 10 caracteres.';
  if (!values.requestingArea.trim()) errors.requestingArea = 'Informe a área solicitante.';
  if (!values.costCenterId) errors.costCenterId = 'Selecione o centro de custo.';
  if (!values.category.trim()) errors.category = 'Informe a categoria da compra.';
  if (values.scope.trim().length < 10) errors.scope = 'Descreva o escopo com pelo menos 10 caracteres.';
  if (values.neededBy && Number.isNaN(Date.parse(`${values.neededBy}T12:00:00`))) {
    errors.neededBy = 'Informe uma data válida.';
  }
  if (values.items.length === 0) errors.items = 'Adicione ao menos um item.';

  values.items.forEach((item, index) => {
    if (item.description.trim().length < 2) errors[`items.${index}.description`] = 'Descreva o item.';
    if (!positiveNumber(item.quantity)) errors[`items.${index}.quantity`] = 'A quantidade deve ser maior que zero.';
    if (!item.unit.trim()) errors[`items.${index}.unit`] = 'Informe a unidade.';
    if (item.estimatedUnitPrice && !positiveNumber(item.estimatedUnitPrice)) {
      errors[`items.${index}.estimatedUnitPrice`] = 'O valor unitário deve ser maior que zero.';
    }
  });

  const needsAccess = values.needsDataAccess || values.needsSystemsAccess || values.needsEnvironmentAccess || values.needsInternalInformation;
  if (needsAccess && values.accessDetails.trim().length < 5) {
    errors.accessDetails = 'Detalhe os acessos necessários.';
  }
  return errors;
}

export function purchaseRequestPayload(values: PurchaseRequestFormValues): CreatePurchaseRequestPayload {
  const items = values.items.map((item) => ({
    description: item.description.trim(),
    quantity: positiveNumber(item.quantity) as number,
    unit: item.unit.trim(),
    ...(positiveNumber(item.estimatedUnitPrice)
      ? { estimatedUnitPrice: positiveNumber(item.estimatedUnitPrice) as number }
      : {}),
    ...(item.specifications.trim() ? { specifications: item.specifications.trim() } : {}),
  }));
  const estimatedValue = items.reduce(
    (total, item) => total + item.quantity * (item.estimatedUnitPrice ?? 0),
    0,
  );

  return {
    title: values.title.trim(),
    justification: values.justification.trim(),
    requestingArea: values.requestingArea.trim(),
    costCenterId: values.costCenterId,
    ...(values.clientId ? { clientId: values.clientId } : {}),
    ...(values.projectId ? { projectId: values.projectId } : {}),
    category: values.category.trim(),
    acquisitionType: values.acquisitionType,
    priority: values.priority,
    ...(values.neededBy ? { neededBy: values.neededBy } : {}),
    ...(estimatedValue > 0 ? { estimatedValue } : {}),
    scope: values.scope.trim(),
    ...(values.technicalRequirements.trim()
      ? { technicalRequirements: values.technicalRequirements.trim() }
      : {}),
    ...(values.functionalRequirements.trim()
      ? { functionalRequirements: values.functionalRequirements.trim() }
      : {}),
    criticality: values.criticality,
    ...(values.budgetStatus ? { budgetStatus: values.budgetStatus } : {}),
    ...(values.impact.trim() ? { impact: values.impact.trim() } : {}),
    accessNeeds: {
      data: values.needsDataAccess,
      systems: values.needsSystemsAccess,
      environments: values.needsEnvironmentAccess,
      internalInformation: values.needsInternalInformation,
      ...(values.accessDetails.trim() ? { details: values.accessDetails.trim() } : {}),
    },
    items,
  };
}

export interface QuotationFormValues {
  supplierId: string;
  currency: string;
  proposalValidUntil: string;
  deliveryDays: string;
  paymentTerms: string;
  commercialNotes: string;
  technicalNotes: string;
  taxesAmount: string;
  freightAmount: string;
  notes: string;
  proposalFile: File | null;
  items: Array<{
    purchaseRequestItemId: string;
    description: string;
    quantity: string;
    unitPrice: string;
    deliveryDays: string;
    notes: string;
    comparable: boolean;
    nonComparableReason: string;
  }>;
}

export function quotationFormFromRequestItems(items: PurchaseRequestItem[]): QuotationFormValues {
  return {
    supplierId: '',
    currency: 'BRL',
    proposalValidUntil: '',
    deliveryDays: '',
    paymentTerms: '',
    commercialNotes: '',
    technicalNotes: '',
    taxesAmount: '',
    freightAmount: '',
    notes: '',
    proposalFile: null,
    items: items.map((item) => ({
      purchaseRequestItemId: item.id,
      description: item.description,
      quantity: String(item.quantity),
      unitPrice: '',
      deliveryDays: '',
      notes: '',
      comparable: true,
      nonComparableReason: '',
    })),
  };
}

export function validateQuotationForm(values: QuotationFormValues): PurchaseFormErrors {
  const errors: PurchaseFormErrors = {};
  if (!values.supplierId) errors.supplierId = 'Selecione o fornecedor.';
  if (!/^[A-Z]{3}$/.test(values.currency)) errors.currency = 'Informe uma moeda ISO de três letras.';
  if (!values.proposalValidUntil) errors.proposalValidUntil = 'Informe a validade da proposta.';
  if (values.paymentTerms.trim().length < 2) errors.paymentTerms = 'Informe as condições de pagamento.';
  if (!values.proposalFile) errors.proposalFile = 'Anexe o arquivo da proposta.';
  if (values.deliveryDays && !positiveNumber(values.deliveryDays)) errors.deliveryDays = 'Informe um prazo maior que zero.';
  if (values.taxesAmount && Number(values.taxesAmount) < 0) errors.taxesAmount = 'Impostos não podem ser negativos.';
  if (values.freightAmount && Number(values.freightAmount) < 0) errors.freightAmount = 'Frete não pode ser negativo.';

  values.items.forEach((item, index) => {
    if (!positiveNumber(item.quantity)) errors[`items.${index}.quantity`] = 'Quantidade inválida.';
    if (!positiveNumber(item.unitPrice)) errors[`items.${index}.unitPrice`] = 'Valor unitário inválido.';
    if (!item.comparable && item.nonComparableReason.trim().length < 5) {
      errors[`items.${index}.nonComparableReason`] = 'Explique por que o item não é comparável.';
    }
  });
  return errors;
}

export function quotationPayload(requestId: string, values: QuotationFormValues): CreateQuotationPayload {
  const optionalMoney = (value: string) => value.trim() ? Number(value.replace(',', '.')) : undefined;
  return {
    purchaseRequestId: requestId,
    supplierId: values.supplierId,
    currency: values.currency,
    proposalValidUntil: values.proposalValidUntil,
    ...(positiveNumber(values.deliveryDays) ? { deliveryDays: positiveNumber(values.deliveryDays) as number } : {}),
    paymentTerms: values.paymentTerms.trim(),
    ...(values.commercialNotes.trim() ? { commercialNotes: values.commercialNotes.trim() } : {}),
    ...(values.technicalNotes.trim() ? { technicalNotes: values.technicalNotes.trim() } : {}),
    ...(optionalMoney(values.taxesAmount) !== undefined ? { taxesAmount: optionalMoney(values.taxesAmount) } : {}),
    ...(optionalMoney(values.freightAmount) !== undefined ? { freightAmount: optionalMoney(values.freightAmount) } : {}),
    ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
    items: values.items.map((item) => ({
      purchaseRequestItemId: item.purchaseRequestItemId,
      quantity: positiveNumber(item.quantity) as number,
      unitPrice: positiveNumber(item.unitPrice) as number,
      ...(positiveNumber(item.deliveryDays) ? { deliveryDays: positiveNumber(item.deliveryDays) as number } : {}),
      ...(item.notes.trim() ? { notes: item.notes.trim() } : {}),
      comparable: item.comparable,
      ...(!item.comparable ? { nonComparableReason: item.nonComparableReason.trim() } : {}),
    })),
  };
}

export function selectionRequiresJustification(
  quotationId: string,
  lowestQuotationId?: string | null,
): boolean {
  return Boolean(lowestQuotationId && quotationId !== lowestQuotationId);
}
